import type { LimitCheckResult } from '@/lib/planLimits';
import { getUpgradeInfo } from '@/lib/planLimits';

interface Props {
  result: LimitCheckResult;
  onClose: () => void;
}

const LIMIT_ICONS: Record<string, string> = {
  users:     'ri-user-add-line',
  contracts: 'ri-file-add-line',
  jobs:      'ri-briefcase-line',
  storage:   'ri-hard-drive-line',
};

const LIMIT_DESCRIPTIONS: Record<string, string> = {
  users:     'You\'ve reached the maximum number of team members for your current plan.',
  contracts: 'You\'ve reached the maximum number of active contracts for your current plan.',
  jobs:      'You\'ve reached the maximum number of active jobs for your current plan.',
  storage:   'You\'ve used all the storage allocated for your current plan.',
};

const PLAN_PERKS: Record<string, string[]> = {
  free: [
    'Up to 1 user',
    '10 active contracts',
    '20 active jobs',
    '512 MB storage',
  ],
  pro: [
    'Up to 5 users',
    '100 active contracts',
    '200 active jobs',
    '5 GB storage',
    'All add-ons available',
    'Priority support',
  ],
  enterprise: [
    'Unlimited users',
    'Unlimited contracts & jobs',
    '50 GB storage',
    'All add-ons included',
    'Dedicated support',
    'Custom domain included',
  ],
};

const NEXT_PLAN_PERKS: Record<string, string[]> = {
  pro:        PLAN_PERKS.pro,
  enterprise: PLAN_PERKS.enterprise,
};

export default function UpgradeModal({ result, onClose }: Props) {
  const { nextPlan, nextPrice, planLabel } = getUpgradeInfo(result.plan_code);
  const icon = LIMIT_ICONS[result.limitKey] ?? 'ri-lock-line';
  const description = LIMIT_DESCRIPTIONS[result.limitKey] ?? 'You\'ve reached a plan limit.';
  const perks = nextPlan ? (NEXT_PLAN_PERKS[nextPlan] ?? []) : [];

  const isStorage = result.limitKey === 'storage';
  const barColor = result.pctUsed >= 90 ? 'bg-red-400' : result.pctUsed >= 70 ? 'bg-amber-400' : 'bg-[#C8A96E]';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-slate-200 w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header gradient */}
        <div className="relative px-6 pt-8 pb-6 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>

          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <div className="w-14 h-14 flex items-center justify-center bg-[#C8A96E]/10 border border-[#C8A96E]/20 rounded-2xl">
                <i className={`${icon} text-2xl text-[#C8A96E]`} />
              </div>
              <div className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-red-500 rounded-full border-2 border-white">
                <i className="ri-lock-line text-white text-[9px]" />
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#C8A96E] mb-0.5">
                Plan Limit Reached
              </p>
              <h2 className="text-[#0f172a] font-bold text-base leading-tight">
                {result.resourceLabel} limit on {planLabel}
              </h2>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Description */}
          <p className="text-slate-500 text-sm leading-relaxed">
            {description}
          </p>

          {/* Usage bar */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600 text-xs font-semibold">Current Usage</span>
              <span className="text-slate-500 text-xs">
                {isStorage
                  ? `${result.current} MB / ${result.max} MB`
                  : `${result.current} / ${result.max}`}
              </span>
            </div>
            <div className="h-2.5 w-full bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barColor}`}
                style={{ width: `${result.pctUsed}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-slate-400 text-[11px]">{planLabel} plan</span>
              <span className={`text-[11px] font-semibold ${result.pctUsed >= 100 ? 'text-red-500' : 'text-amber-500'}`}>
                {result.pctUsed}% used
              </span>
            </div>
          </div>

          {/* Next plan perks */}
          {nextPlan && perks.length > 0 && (
            <div>
              <p className="text-slate-600 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
                <i className="ri-vip-crown-line text-[#C8A96E]" />
                Upgrade to {nextPlan.charAt(0).toUpperCase() + nextPlan.slice(1)}
                {nextPrice && (
                  <span className="ml-auto text-[#C8A96E] font-bold normal-case">{nextPrice}</span>
                )}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {perks.map((perk) => (
                  <div key={perk} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center bg-emerald-50 rounded-full shrink-0">
                      <i className="ri-check-line text-emerald-500 text-[10px]" />
                    </div>
                    <span className="text-slate-600 text-xs">{perk}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No next plan (enterprise) */}
          {!nextPlan && (
            <div className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-200 bg-slate-50">
              <i className="ri-customer-service-2-line text-slate-400 text-sm mt-0.5 shrink-0" />
              <p className="text-slate-500 text-xs leading-relaxed">
                You're on the Enterprise plan. Contact your platform Super Admin to increase specific limits.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-slate-200 text-slate-500 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
          {nextPlan && (
            <button
              onClick={onClose}
              className="flex-1 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-vip-crown-line" />
              Contact Admin to Upgrade
            </button>
          )}
        </div>
      </div>
    </div>
  );
}